import { NgClass } from '@angular/common';
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  linkedSignal,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatSelect } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { AppInsightsServiceInjector } from '@next/core-lib/app-insights';
import { NotificationEmptyComponent } from '@next/core-lib/components';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { distinctUntilChanged, filter } from 'rxjs';

import { defaultBlightDayRange } from 'src/app/blight-v2/shared/helpers/blight-helpers';
import { DateSelectorComponent } from 'src/app/shared/components/date-selector.component/date-selector.component';
import { DiseaseChartDay } from 'src/app/shared/components/disease-insight-chart/models';
import { CHART_CONFIG } from 'src/app/shared/components/disease-insight-chart/base-chart.config';
import { DiseaseInsightChartComponent } from 'src/app/shared/components/disease-insight-chart/disease-insight-chart.component';
import { CropFieldAdvice } from 'src/app/shared/components/disease-overview-table.component/models/models';
import { RecalculateButtonComponent } from 'src/app/shared/components/recalculate-button/recalculate-button.component';
import { DateService } from 'src/app/shared/services/date.service';
import { CropYearService } from 'src/app/shared/services/crop-year.service';
import { longestCropFieldText } from 'src/app/shared/utils/panel-min-width.utils';

import { BlightAIIdentifiers } from '../../../../shared/constants';
import { BlightCropFieldAdvice, CropFieldRegistration, SprayAdvice } from '../../../../models/models';
import { mapBlightDaysToChartDays } from '../../../../models/mappers';
import { BlightSignalStore } from '../../../../signal-store/blight-signal-store';
import { ActionMenuComponent } from '../../../../shared/components/action-menu.component';
import { BlightInsightToolbarComponent } from './components/blight-details-toolbar/blight-details-toolbar.component';
import { BlightChartConfig } from './components/blight-insight-chart/blight-hichart.config';

@Component({
  selector: 'app-blight-insight-page',
  templateUrl: './blight-insight-page.component.html',
  styleUrl: './blight-insight-page.component.scss',
  providers: [{ provide: CHART_CONFIG, useClass: BlightChartConfig }],
  imports: [
    NgClass,
    ReactiveFormsModule,
    MatSelect,
    MatOption,
    MatButton,
    MatIcon,
    MatMenuTrigger,
    DateSelectorComponent,
    BlightInsightToolbarComponent,
    ActionMenuComponent,
    DiseaseInsightChartComponent,
    RecalculateButtonComponent,
    NotificationEmptyComponent,
    TranslatePipe,
  ],
})
export class BlightInsightPageComponent {
  private readonly cropYearService = inject(CropYearService);
  protected readonly isCropYearReadOnly = this.cropYearService.isReadOnly;
  protected readonly store = inject(BlightSignalStore);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly dateService = inject(DateService);
  private readonly ai = inject(AppInsightsServiceInjector);
  private readonly localeService = inject(LocaleService);

  disableCropFieldSelector = input(false);
  preselectedCropFieldId = input<number | undefined>(undefined);

  protected readonly cropFields = linkedSignal<BlightCropFieldAdvice[] | undefined, BlightCropFieldAdvice[]>({
    source: () => (this.store.blightDataIsLoading() ? undefined : this.store.visibleBlightAdvices()),
    computation: (source, previous) => source ?? previous?.value ?? [],
  });

  protected readonly longestOptionText = computed(() => longestCropFieldText(this.cropFields()));
  protected readonly isEmbeddedCropFieldUnavailable = computed(
    () =>
      this.disableCropFieldSelector() &&
      this.preselectedCropFieldId() !== undefined &&
      !this.store.blightDataIsLoading() &&
      !this.store.participatingCropFieldsIsLoading() &&
      !this.cropFields().some((cropField) => cropField.cropFieldId === this.preselectedCropFieldId()),
  );

  protected readonly minimalDate = this.store.blightMinAdviceDate;
  protected readonly selectedCropFieldAdvice = this.store.selectedCropFieldAdvice;
  protected readonly selectedCropField = linkedSignal<
    CropFieldRegistration | undefined,
    CropFieldRegistration | undefined
  >({
    source: () => {
      if (this.store.blightDataIsLoading() || this.store.selectedCropFieldRegistrationIsLoading()) return undefined;
      return this.store.selectedCropFieldRegistrationModel();
    },
    computation: (source, previous) => source ?? previous?.value,
  });
  protected readonly hasAdvices = linkedSignal<boolean | undefined, boolean>({
    source: () => {
      if (this.store.blightDataIsLoading()) return undefined;
      return this.store.hasSelectedCropFieldAdvices();
    },
    computation: (source, previous) => source ?? previous?.value ?? false,
  });
  protected readonly chartDays = linkedSignal<DiseaseChartDay[] | undefined, DiseaseChartDay[]>({
    source: () => {
      if (this.store.blightDataIsLoading() || this.store.selectedCropFieldDetailsIsLoading()) return undefined;
      const chartDetails = this.store.selectedCropFieldChartDetails();
      return mapBlightDaysToChartDays(chartDetails?.[0]?.blightResult?.days ?? [], this.store.selectedDates());
    },
    computation: (source, previous) => source ?? previous?.value ?? [],
  });

  protected readonly maximalDate = computed(() =>
    this.dateService.daysInTheFuture(defaultBlightDayRange.daysInFuture, this.store.cropYear()),
  );
  protected readonly isReadOnly = computed(() => !this.selectedCropField() || this.isCropYearReadOnly());
  protected readonly showHarvestedNoAdvice = computed(() => {
    const harvestDate = this.selectedCropField()?.harvestDate ?? this.selectedCropFieldAdvice()?.harvestDate;
    if (!harvestDate || !this.dateService.isInThePast(harvestDate)) {
      return false;
    }

    const selectedDates = this.store.selectedDates();
    const hasDateAfterHarvest = selectedDates.some((date) =>
      this.dateService.isDateLaterThanOrEquals(date, this.dateService.dayAfter(harvestDate)),
    );
    const hasAdviceInSelectedRange = (this.selectedCropFieldAdvice()?.advices ?? []).some(
      (advice) =>
        advice.sprayAdvice !== SprayAdvice.noAdvice &&
        selectedDates.some((date) => this.dateService.isDateSameDay(advice.date, date)),
    );

    return hasDateAfterHarvest && !hasAdviceInSelectedRange;
  });
  protected readonly isRecalculationDisabled = linkedSignal<boolean | undefined, boolean>({
    source: () => {
      if (this.store.blightDataIsLoading()) return undefined;
      return (
        this.store.isBlightCropFieldsSyncing() ||
        !this.store.selectedCropFieldId() ||
        this.isReadOnly() ||
        this.store.isSelectedCropFieldIneligibleForRecalculation()
      );
    },
    computation: (source, previous) => source ?? previous?.value ?? true,
  });

  protected readonly selectedAdvice = computed((): CropFieldAdvice[] => {
    const advice = this.store.selectedCropFieldAdvice();
    return advice ? [advice] : [];
  });

  protected readonly adviceForChart = linkedSignal<CropFieldAdvice | undefined, CropFieldAdvice | undefined>({
    source: () => {
      if (this.store.blightDataIsLoading() || this.store.selectedCropFieldDetailsIsLoading()) return undefined;
      return this.store.selectedCropFieldAdvice();
    },
    computation: (source, previous) => source ?? previous?.value,
  });

  protected readonly actionDate = signal<Date | undefined>(undefined);

  protected readonly form = new FormGroup({
    cropField: new FormControl<number | undefined>(undefined),
  });

  @ViewChild('menuTrigger', { read: MatMenuTrigger }) private readonly menuTrigger!: MatMenuTrigger;
  @ViewChild('menuTrigger', { read: ElementRef }) private readonly menuTriggerEl!: ElementRef<HTMLElement>;

  constructor() {
    this.initializeCropFieldSelection();
    this.subscribeToCropFieldChange();
  }

  protected onSelectedDatesChanged(dates: Date[]): void {
    this.store.patchSelectedDates(dates);
  }

  protected onRecalculate(): void {
    const advice = this.store.selectedCropFieldAdvice();
    if (!advice || this.isReadOnly()) {
      return;
    }

    this.ai.trackEvent(BlightAIIdentifiers.recalc, {});
    this.store.requestBlightRecalculation([{ cropFieldId: advice.cropFieldId, year: advice.cropYear }]);
  }

  protected onHeaderActionMenuOpened(): void {
    this.actionDate.set(undefined);
  }

  protected onActionTriggered({ event, date }: { event: MouseEvent; date: Date }): void {
    this.actionDate.set(date);
    const el = this.menuTriggerEl.nativeElement;
    el.style.left = `${event.clientX + 10}px`;
    el.style.top = `${event.clientY + 10}px`;
    this.menuTrigger.openMenu();
  }

  protected getUnprotectedGrowthTooltip(day: DiseaseChartDay): string {
    return `${this.localeService.translate('blight.blightChart.unprotectedLeafLayers')} ${(day.unprotectedGrowth ?? 0).toFixed(1)}`;
  }

  private initializeCropFieldSelection(): void {
    effect(() => {
      const cropFields = this.cropFields();
      const preselectedCropFieldId = this.preselectedCropFieldId();
      const isEmbeddedSelection = this.disableCropFieldSelector() && preselectedCropFieldId !== undefined;

      if (!cropFields.length) {
        if (isEmbeddedSelection) {
          this.clearSelectedCropField();
        }
        return;
      }

      const currentSelected = this.store.selectedCropFieldId();

      if (isEmbeddedSelection) {
        const selectedCropFieldId = cropFields.find(
          (cropField) => cropField.cropFieldId === preselectedCropFieldId,
        )?.cropFieldId;

        if (!selectedCropFieldId) {
          this.clearSelectedCropField();
          return;
        }

        this.selectCropField(selectedCropFieldId, currentSelected);
        return;
      }

      if (currentSelected && cropFields.some((cropField) => cropField.cropFieldId === currentSelected)) {
        const currentFormValue = this.form.controls.cropField.value;
        if (currentFormValue !== currentSelected) {
          untracked(() => {
            this.form.controls.cropField.setValue(currentSelected, { emitEvent: false });
            void this.router.navigate([], {
              queryParams: { cropFieldId: currentSelected },
              queryParamsHandling: 'merge',
            });
          });
        }
        return;
      }

      const cropFieldQueryParam = this.route.snapshot.queryParamMap.get('cropFieldId');
      const queryParamCropFieldId = cropFieldQueryParam ? +cropFieldQueryParam : undefined;
      const targetId = preselectedCropFieldId ?? queryParamCropFieldId ?? cropFields[0]?.cropFieldId;
      const selectedCropFieldId =
        cropFields.find((cropField) => cropField.cropFieldId === targetId)?.cropFieldId ?? cropFields[0]?.cropFieldId;

      if (selectedCropFieldId) {
        this.selectCropField(selectedCropFieldId, currentSelected);
      }
    });
  }

  private selectCropField(selectedCropFieldId: number, currentSelected = this.store.selectedCropFieldId()): void {
    const selectionChanged = currentSelected !== selectedCropFieldId;
    const formValueChanged = this.form.controls.cropField.value !== selectedCropFieldId;

    if (!selectionChanged && !formValueChanged) {
      return;
    }

    untracked(() => {
      if (selectionChanged) {
        this.store.selectCropField(selectedCropFieldId);
      }
      if (formValueChanged) {
        this.form.controls.cropField.setValue(selectedCropFieldId, { emitEvent: false });
      }
      void this.router.navigate([], {
        queryParams: { cropFieldId: selectedCropFieldId },
        queryParamsHandling: 'merge',
      });
    });
  }

  private clearSelectedCropField(): void {
    const selectionChanged = this.store.selectedCropFieldId() !== undefined;
    const formValueChanged = this.form.controls.cropField.value !== undefined;
    const hasCropFieldQueryParam = this.route.snapshot.queryParamMap.get('cropFieldId') !== null;

    if (!selectionChanged && !formValueChanged && !hasCropFieldQueryParam) {
      return;
    }

    untracked(() => {
      if (selectionChanged) {
        this.store.clearSelectedCropField();
      }
      if (formValueChanged) {
        this.form.controls.cropField.setValue(undefined, { emitEvent: false });
      }
      if (hasCropFieldQueryParam) {
        void this.router.navigate([], {
          queryParams: { cropFieldId: null },
          queryParamsHandling: 'merge',
        });
      }
    });
  }

  private subscribeToCropFieldChange(): void {
    this.form.controls.cropField.valueChanges
      .pipe(
        takeUntilDestroyed(),
        filter((cropFieldId): cropFieldId is number => !!cropFieldId),
        distinctUntilChanged(),
      )
      .subscribe((cropFieldId) => {
        this.store.selectCropField(cropFieldId);
        void this.router.navigate([], {
          queryParams: { cropFieldId },
          queryParamsHandling: 'merge',
        });
      });
  }
}
